import { API_URL, extension_id } from '../utils/api';
import { collection, getDocs, query, where, orderBy, doc, getDoc, setDoc, updateDoc, limit, deleteDoc } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';

class EstimateApis {
  constructor(storeId, userId, email) {
    this.storeId = storeId;
    this.userId = userId;
    this.email = email;
  }

  // Get store estimates - using Firestore like mobile app
  async getStoreEstimates(status = "draft") {
    try {
      console.log('🔍 EstimateApis.getStoreEstimates() - Fetching estimates for user:', this.userId, 'email:', this.email, 'status:', status);
      console.log('🔍 EstimateApis.getStoreEstimates() - Firebase path will be:', `dealer-portal-users/${this.email}/estimates`);
      
      // Reference to the Estimates collection under a specific user
      const estimatesRef = collection(db, "dealer-portal-users", this.email, "estimates");
      const q = query(
        estimatesRef,
        where("status", "==", status),
        orderBy("createdAt", "desc")
      );

      console.log('🔍 EstimateApis.getStoreEstimates() - Executing Firestore query...');
      
      // Fetch all store estimates
      const snapshot = await getDocs(q);

      console.log('🔍 EstimateApis.getStoreEstimates() - Query result:', {
        empty: snapshot.empty,
        size: snapshot.size
      });

      // If there are no store estimates, return an empty array
      if (snapshot.empty) {
        console.log('📝 EstimateApis.getStoreEstimates() - No estimates found');
        return [];
      }

      // Map the documents to Estimate objects
      const estimates = snapshot.docs.map((docSnapshot) => {
        const estimateData = docSnapshot.data();
        console.log('📝 EstimateApis.getStoreEstimates() - Estimate data:', estimateData);

        return {
          id: docSnapshot.id,
          estimateNo: estimateData.estimateNo,
          customerID: estimateData.customerID,
          userId: estimateData.userId,
          customerName: estimateData.customerName,
          cartID: estimateData.cartID,
          orderID: estimateData.orderID,
          estimate_profit: estimateData.estimate_profit,
          estimateData: estimateData.estimateData || {},
          createdAt: estimateData.createdAt,
          validUntil: estimateData.validUntil,
          isConvertedToOrder: estimateData.isConvertedToOrder,
        };
      });

      console.log('✅ EstimateApis.getStoreEstimates() - Successfully fetched', estimates.length, 'estimates');
      return estimates;
    } catch (error) {
      console.error('❌ EstimateApis.getStoreEstimates() - Error fetching estimates:', error);
      return [];
    }
  }

  // Get a specific estimate - using Firestore like mobile app
  async getEstimate(estimateId) {
    try {
      console.log('🔍 EstimateApis.getEstimate() - Fetching estimate:', estimateId);
      
      // Reference to the specific estimate document
      const estimateRef = doc(db, "dealer-portal-users", this.email, "estimates", estimateId);

      // Fetch the estimate document
      const snapshot = await getDoc(estimateRef);

      if (!snapshot.exists()) {
        console.log('📝 EstimateApis.getEstimate() - Estimate not found');
        return null;
      }

      const data = snapshot.data();
      console.log('📝 EstimateApis.getEstimate() - Base estimate data:', data);

      const baseEstimate = {
        id: snapshot.id,
        estimateNo: data.estimateNo,
        customerID: data.customerID,
        userId: data.userId,
        customerName: data.customerName,
        cartID: data.cartID,
        orderID: data.orderID,
        estimate_profit: data.estimate_profit,
        notes: data.notes || { customer_note: "", terms_and_conditions: "" },
        payment_received: data.payment_received || 0,
        status: data.status,
        createdAt: data.createdAt,
        validUntil: data.validUntil,
        isConvertedToOrder: data.isConvertedToOrder,
        estimateData: {},
      };

      // Fetch cart data from API based on status (like mobile app)
      if (data.status === 'draft') {
        console.log('📦 EstimateApis.getEstimate() - Fetching draft cart data...');
        const cartResponse = await fetch(`${API_URL}/customers/${data.customerID}/carts/${data.cartID}`);
        
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          console.log('📦 EstimateApis.getEstimate() - Cart data received:', cartData);
          
          // Calculate total price from cart items
          const cartItems = cartData.customerCart?.cart_items || [];
          let totalPrice = 0;
          cartItems.forEach(item => {
            totalPrice += parseFloat(item.customer_item_total || item.item_total || 0);
          });

          baseEstimate.estimateData = {
            products: cartItems,
            total_price: totalPrice || parseFloat(cartData.customerCart?.cart_total || 0),
            customer: {
              id: cartData.customerCart?.customer?.id,
              customer_name: cartData.customerCart?.tag || data.customerName,
              customer_email: cartData.customerCart?.customer?.email || "",
              customer_phone: cartData.customerCart?.customer?.phone || "",
            },
            discount: parseFloat(cartData.discount?.discount_rate || 0),
            discount_type: cartData.discount?.discount_type || 'percentage',
            tax: parseFloat(cartData.tax || 0),
          };
        }
      } else if (data.status === 'saved') {
        console.log('📦 EstimateApis.getEstimate() - Fetching saved order cart data...');
        const orderResponse = await fetch(`${API_URL}/customers/${data.customerID}/order-carts/${data.orderID}`);
        
        if (orderResponse.ok) {
          const orderData = await orderResponse.json();
          console.log('📦 EstimateApis.getEstimate() - Order cart data received:', orderData);
          
          // Calculate total price from cart items
          const orderCartItems = orderData.orderCart?.cart_items || [];
          let orderTotalPrice = 0;
          orderCartItems.forEach(item => {
            orderTotalPrice += parseFloat(item.customer_item_total || item.item_total || 0);
          });

          baseEstimate.estimateData = {
            products: orderCartItems,
            total_price: orderTotalPrice || parseFloat(orderData.orderCart?.cart_total || 0),
            customer: {
              id: orderData.orderCart?.customer?.id,
              customer_name: orderData.orderCart?.tag || data.customerName,
              customer_email: orderData.orderCart?.customer?.email || "",
              customer_phone: orderData.orderCart?.customer?.phone || "",
            },
            discount: parseFloat(orderData.discount?.discount_rate || 0),
            discount_type: orderData.discount?.discount_type || 'percentage',
            tax: parseFloat(orderData.tax || 0),
          };
          
          baseEstimate.isConvertedToOrder = orderData.isConvertedToOrder;
        }
      }

      console.log('✅ EstimateApis.getEstimate() - Successfully fetched estimate with cart data');
      return baseEstimate;
    } catch (error) {
      console.error('❌ EstimateApis.getEstimate() - Error fetching estimate:', error);
      return null;
    }
  }

  // Create a new estimate - using Firestore like mobile app
  async createEstimate({ order_tag, profit }) {
    try {
      console.log('🔍 EstimateApis.createEstimate() - Creating estimate with:', { order_tag, profit });
      
      // 1. Create cart via API (exactly like mobile app)
      const cartResponse = await fetch(`${API_URL}/customers/${this.userId}/carts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          extension_id: extension_id,
          tag: order_tag,
        }),
      });

      if (!cartResponse.ok) {
        const errorText = await cartResponse.text();
        console.error('Cart creation failed:', cartResponse.status, errorText);
        throw new Error(`Failed to create cart: ${cartResponse.status} ${errorText}`);
      }

      const cartData = await cartResponse.json();
      if (!cartData.success) {
        throw new Error(cartData.message || 'Cart creation failed');
      }
      
      const cartId = cartData.cart_id;
      console.log('📦 EstimateApis.createEstimate() - Cart created:', cartId);

      // 2. Create Firestore document (like mobile app)
      console.log('📝 EstimateApis.createEstimate() - Creating Firestore document for user:', this.userId);
      console.log('📝 EstimateApis.createEstimate() - Using email for Firebase path:', this.email);
      console.log('📝 EstimateApis.createEstimate() - Firebase path will be:', `dealer-portal-users/${this.email}/estimates`);
      const estimatesRef = collection(db, "dealer-portal-users", this.email, "estimates");
      const newEstimateRef = doc(estimatesRef);

      const estimateData = {
        id: this.userId, // Use customer ID as the id field
        userId: this.userId,
        customerID: Number(this.userId),
        customerName: order_tag,
        cartID: cartId,
        orderID: null,
        storeID: this.storeId,
        estimateNo: await this.getNewEstimateNo(),
        payment_received: 0,
        estimate_profit: profit || 50,
        notes: {
          customer_note: "Thank you for your business!",
          terms_and_conditions: "",
          order_note: "",
        },
        isConvertedToOrder: false,
        status: "draft",
        createdAt: new Date(),
        validUntil: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 days from now
      };

      console.log('📝 EstimateApis.createEstimate() - Saving estimate data:', estimateData);
      await setDoc(newEstimateRef, estimateData);
      console.log('✅ EstimateApis.createEstimate() - Estimate created with ID:', newEstimateRef.id);
      
      return newEstimateRef.id;
    } catch (error) {
      console.error('❌ EstimateApis.createEstimate() - Error:', error);
      console.error('❌ EstimateApis.createEstimate() - Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // Get new estimate number
  async getNewEstimateNo() {
    try {
      const estimatesRef = collection(db, "dealer-portal-users", this.email, "estimates");
      const q = query(estimatesRef, orderBy("estimateNo", "desc"), limit(1));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return 1;
      } else {
        const latestEstimate = snapshot.docs[0].data();
        const latestEstimateNo = latestEstimate.estimateNo;
        return parseInt(latestEstimateNo) + 1;
      }
    } catch (error) {
      console.error('Error getting new estimate number:', error);
      return 1;
    }
  }

  // Save estimate (convert to order) - like mobile app
  async saveEstimate(paymentReceived, customerNote, termsAndConditions, cartId, customerId, estimateId, customerProfit, orderNote = '') {
    try {
      console.log('🔍 EstimateApis.saveEstimate() - Saving estimate:', estimateId);

      // 1. Push order_note onto the customer_cart so it propagates through
      //    convertOrderCart -> order_cart -> orders.
      if (orderNote !== undefined && orderNote !== null) {
        const noteResponse = await fetch(`${API_URL}/customers/${this.userId}/carts/${cartId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_note: orderNote }),
        });
        if (!noteResponse.ok) {
          const errorText = await noteResponse.text();
          console.warn('Failed to persist order_note to cart before conversion:', errorText);
        }
      }

      // 2. Convert cart to order cart via API
      const response = await fetch(`${API_URL}/customers/${this.userId}/carts/${cartId}/convertOrderCart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          extension_id: extension_id,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to convert cart to order');
      }

      const orderData = await response.json();
      console.log('📦 EstimateApis.saveEstimate() - Order cart created:', orderData);

      // 3. Update Firestore document
      const estimateRef = doc(db, "dealer-portal-users", this.email, "estimates", estimateId);
      await updateDoc(estimateRef, {
        payment_received: paymentReceived,
        estimate_profit: customerProfit || 0,
        notes: {
          customer_note: customerNote,
          terms_and_conditions: termsAndConditions,
          order_note: orderNote || '',
        },
        isConvertedToOrder: true,
        status: "saved",
        orderID: orderData.orderCart.id,
      });

      console.log('✅ EstimateApis.saveEstimate() - Estimate saved successfully');
    } catch (error) {
      console.error('❌ EstimateApis.saveEstimate() - Error:', error);
      throw error;
    }
  }

  // Save order_note onto a draft estimate's customer_cart + Firestore (no cart conversion)
  async updateEstimateOrderNote(cartId, estimateId, orderNote) {
    try {
      const noteResponse = await fetch(`${API_URL}/customers/${this.userId}/carts/${cartId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_note: orderNote }),
      });
      if (!noteResponse.ok) {
        const errorText = await noteResponse.text();
        throw new Error(`Failed to save order note: ${errorText}`);
      }

      const estimateRef = doc(db, "dealer-portal-users", this.email, "estimates", estimateId);
      const snap = await getDoc(estimateRef);
      const existingNotes = snap.exists() ? (snap.data().notes || {}) : {};
      await updateDoc(estimateRef, {
        notes: { ...existingNotes, order_note: orderNote || '' },
      });
    } catch (error) {
      console.error('❌ EstimateApis.updateEstimateOrderNote() - Error:', error);
      throw error;
    }
  }

  // Clean up orphaned estimates that exist in Firebase but not in supply circle DB
  async cleanupOrphanedEstimates() {
    try {
      console.log('🧹 EstimateApis.cleanupOrphanedEstimates() - Starting cleanup...');
      
      console.log('🧹 EstimateApis.cleanupOrphanedEstimates() - Email:', this.email); 
      // Get all estimates from Firebase
      const estimatesRef = collection(db, "dealer-portal-users", this.email, "estimates");
      const snapshot = await getDocs(estimatesRef);
      
      if (snapshot.empty) {
        console.log('ℹ️ EstimateApis.cleanupOrphanedEstimates() - No estimates found in Firebase');
        return [];
      }
      
      const orphanedEstimates = [];
      
      // Check each estimate
      for (const docSnapshot of snapshot.docs) {
        const estimateData = docSnapshot.data();
        const estimateId = docSnapshot.id;
        
        // IMPORTANT: Skip active orders (saved estimates) from cleanup!
        // Active orders have converted carts to order carts, so original cart won't exist
        if (estimateData.status === "saved" || estimateData.isConvertedToOrder) {
          console.log(`⏭️ EstimateApis.cleanupOrphanedEstimates() - Skipping active order: ${estimateId}`);
          continue;
        }
        
        // Skip if no cartID (can't verify)
        if (!estimateData.cartID || !estimateData.customerID) {
          console.log(`⏭️ EstimateApis.cleanupOrphanedEstimates() - Skipping estimate with missing cart/customer data: ${estimateId}`);
          continue;
        }
        
        // Only check draft estimates (status: "draft")
        if (estimateData.status !== "draft") {
          console.log(`⏭️ EstimateApis.cleanupOrphanedEstimates() - Skipping non-draft estimate: ${estimateId} (status: ${estimateData.status})`);
          continue;
        }
        
        try {
          // Check if the cart still exists in supply circle API (only for draft estimates)
          const cartResponse = await fetch(`${API_URL}/customers/${estimateData.customerID}/carts/${estimateData.cartID}`);
          
          if (!cartResponse.ok && cartResponse.status === 404) {
            // Cart doesn't exist, this DRAFT estimate is orphaned
            console.log(`🗑️ EstimateApis.cleanupOrphanedEstimates() - Found orphaned DRAFT estimate: ${estimateId}`);
            orphanedEstimates.push({
              id: estimateId,
              data: estimateData
            });
            
            // Delete the orphaned estimate from Firebase
            await deleteDoc(docSnapshot.ref);
            console.log(`✅ EstimateApis.cleanupOrphanedEstimates() - Deleted orphaned DRAFT estimate: ${estimateId}`);
          } else if (cartResponse.ok) {
            console.log(`✅ EstimateApis.cleanupOrphanedEstimates() - Draft estimate ${estimateId} cart verified as existing`);
          }
        } catch (error) {
          console.log(`⚠️ EstimateApis.cleanupOrphanedEstimates() - Could not verify estimate ${estimateId}:`, error);
        }
      }
      
      console.log(`✅ EstimateApis.cleanupOrphanedEstimates() - Cleanup completed. Removed ${orphanedEstimates.length} orphaned estimates`);
      return orphanedEstimates;
    } catch (error) {
      console.error('❌ EstimateApis.cleanupOrphanedEstimates() - Error:', error);
      return [];
    }
  }

  // Update customer profit for draft estimate
  async updateCustomerProfit(estimateId, customerProfit) {
    try {
      console.log('🔍 EstimateApis.updateCustomerProfit() - Updating profit for estimate:', estimateId);
      
      const estimateRef = doc(db, "dealer-portal-users", this.email, "estimates", estimateId);
      await updateDoc(estimateRef, {
        estimate_profit: customerProfit || 0,
      });

      console.log('✅ EstimateApis.updateCustomerProfit() - Profit updated successfully');
    } catch (error) {
      console.error('❌ EstimateApis.updateCustomerProfit() - Error:', error);
      throw error;
    }
  }

  // Update an estimate
  async updateEstimate(estimateId, estimateData) {
    try {
      const response = await fetch(`${API_URL}/estimates/${estimateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(estimateData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.estimate;
      } else {
        const error = await response.json();
        console.error('Error updating estimate:', error);
        throw new Error(error.message || 'Failed to update estimate');
      }
    } catch (error) {
      console.error('Error updating estimate:', error);
      throw error;
    }
  }

  // Delete an estimate - using Firestore like mobile app
  async deleteEstimate(estimateId) {
    try {
      console.log('🗑️ EstimateApis.deleteEstimate() - Deleting estimate:', estimateId);
      
      // Reference to the specific estimate document
      const estimateRef = doc(db, "dealer-portal-users", this.email, "estimates", estimateId);
      
      // First check if estimate exists in Firebase
      const estimateDoc = await getDoc(estimateRef);
      if (!estimateDoc.exists()) {
        console.log('⚠️ EstimateApis.deleteEstimate() - Estimate not found in Firebase, may have been already deleted');
        return true; // Consider it successfully deleted if it doesn't exist
      }
      
      const estimateData = estimateDoc.data();
      console.log('📦 EstimateApis.deleteEstimate() - Estimate data:', estimateData);
      
      // Step 1: Delete from Firestore first (most important for UI)
      await deleteDoc(estimateRef);
      console.log('✅ EstimateApis.deleteEstimate() - Estimate deleted from Firebase');
      
      // Step 2: Try to clean up associated cart from supply circle API (optional)
      if (estimateData.cartID && estimateData.customerID) {
        try {
          console.log('🗑️ EstimateApis.deleteEstimate() - Attempting to delete cart from supply circle API:', estimateData.cartID);
          const cartResponse = await fetch(`${API_URL}/customers/${estimateData.customerID}/carts/${estimateData.cartID}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (cartResponse.ok) {
            console.log('✅ EstimateApis.deleteEstimate() - Cart deleted from supply circle API');
          } else {
            const errorText = await cartResponse.text();
            console.log('⚠️ EstimateApis.deleteEstimate() - Cart deletion from supply circle API failed:', errorText);
            console.log('ℹ️ This is not critical since Firebase estimate was already deleted');
          }
        } catch (cartError) {
          console.log('⚠️ EstimateApis.deleteEstimate() - Cart deletion from supply circle API failed:', cartError);
          console.log('ℹ️ This is not critical since Firebase estimate was already deleted');
        }
      }
      
      // Step 3: If estimate was converted to an order, try to clean up order data
      if (estimateData.orderID) {
        try {
          console.log('🗑️ EstimateApis.deleteEstimate() - Attempting to clean up order cart:', estimateData.orderID);
          const orderResponse = await fetch(`${API_URL}/customers/${estimateData.customerID}/order-carts/${estimateData.orderID}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (orderResponse.ok) {
            console.log('✅ EstimateApis.deleteEstimate() - Order cart cleaned up');
          } else {
            console.log('⚠️ EstimateApis.deleteEstimate() - Order cart cleanup failed, but continuing');
          }
        } catch (orderError) {
          console.log('⚠️ EstimateApis.deleteEstimate() - Order cart cleanup failed:', orderError);
        }
      }
      
      console.log('✅ EstimateApis.deleteEstimate() - Estimate deletion completed');
      return true;
    } catch (error) {
      console.error('❌ EstimateApis.deleteEstimate() - Error:', error);
      
      // If it's a permission error or network error, still return true if the estimate doesn't exist
      try {
        const estimateRef = doc(db, "dealer-portal-users", this.email, "estimates", estimateId);
        const checkDoc = await getDoc(estimateRef);
        if (!checkDoc.exists()) {
          console.log('ℹ️ EstimateApis.deleteEstimate() - Estimate no longer exists, considering deletion successful');
          return true;
        }
      } catch (checkError) {
        console.log('⚠️ EstimateApis.deleteEstimate() - Could not verify estimate existence:', checkError);
      }
      
      throw error;
    }
  }

  // Convert estimate to order
  async convertToOrder(estimateId) {
    try {
      const response = await fetch(`${API_URL}/estimates/${estimateId}/convert-to-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.order;
      } else {
        const error = await response.json();
        console.error('Error converting estimate to order:', error);
        throw new Error(error.message || 'Failed to convert estimate to order');
      }
    } catch (error) {
      console.error('Error converting estimate to order:', error);
      throw error;
    }
  }

  // Get estimate statistics
  async getEstimateStats() {
    try {
      const response = await fetch(`${API_URL}/estimates/stats/${this.storeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.stats || {};
      } else {
        const error = await response.json();
        console.error('Error fetching estimate stats:', error);
        return {};
      }
    } catch (error) {
      console.error('Error fetching estimate stats:', error);
      return {};
    }
  }

  // Create discount - matching mobile app functionality
  async createDiscount(discount_rate, discount_type, customer_id) {
    try {
      console.log('🔍 EstimateApis.createDiscount() - Creating discount:', { discount_rate, discount_type, customer_id });
      
      const response = await fetch(`${API_URL}/customers/${customer_id}/checkout-discounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount_rate: discount_rate,
          discount_type: discount_type,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create discount');
      }

      const data = await response.json();
      console.log('✅ EstimateApis.createDiscount() - Discount created:', data);
      return data.discount;
    } catch (error) {
      console.error('❌ EstimateApis.createDiscount() - Error:', error);
      throw new Error("Error creating discount: " + error.message);
    }
  }

  // Create tax - matching mobile app functionality
  async createTax(tax_rate, customer_id) {
    try {
      console.log('🔍 EstimateApis.createTax() - Creating tax:', { tax_rate, customer_id });
      
      const response = await fetch(`${API_URL}/customers/${customer_id}/customer-tax-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tax_rate: tax_rate,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create tax');
      }

      const data = await response.json();
      console.log('✅ EstimateApis.createTax() - Tax created:', data);
      return data.tax;
    } catch (error) {
      console.error('❌ EstimateApis.createTax() - Error:', error);
      throw new Error("Error creating tax: " + error.message);
    }
  }
}

export default EstimateApis; 